<template>
  <div class="bj-admin-config">
    <!-- Header -->
    <div class="config-header">
      <h2 class="config-title">{{ $t('admin.config.blackjack.title') }}</h2>
      <p class="config-subtitle">{{ $t('admin.config.blackjack.subtitle') }}</p>
    </div>

    <div v-if="loading" class="loading-state">{{ $t('admin.config.loading') }}</div>
    <div v-else-if="loadError" class="error-banner">{{ loadError }}</div>

    <form v-else class="settings-form" @submit.prevent="saveConfig">

      <!-- Deck Settings -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.blackjack.section_deck') }}</h3>

        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.blackjack.label_deck_count') }}
            <select v-model.number="form.deck_count" class="form-select">
              <option :value="1">1 deck (156 cards)</option>
              <option :value="2">2 decks (104 cards)</option>
              <option :value="4">4 decks (208 cards)</option>
              <option :value="6">6 decks (312 cards) — default</option>
              <option :value="8">8 decks (416 cards)</option>
            </select>
            <span class="form-hint">{{ $t('admin.config.blackjack.hint_deck_count') }}</span>
          </label>

          <label class="form-label">
            {{ $t('admin.config.blackjack.label_penetration') }}
            <input
              v-model.number="form.penetration_fraction"
              type="number"
              step="0.05"
              min="0.1"
              max="0.99"
              class="form-input"
              required
            />
            <span class="form-hint">{{ $t('admin.config.blackjack.hint_penetration') }}</span>
          </label>
        </div>
      </section>

      <!-- Payout Rules -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.blackjack.section_payout') }}</h3>

        <div class="form-row">
          <div class="form-label">
            {{ $t('admin.config.blackjack.label_blackjack_payout') }}
            <div class="radio-group">
              <label class="radio-label">
                <input v-model="form.blackjack_payout" type="radio" value="3:2" class="radio-input" />
                <span>3:2 — Standard (pays $15 on $10 bet)</span>
              </label>
              <label class="radio-label">
                <input v-model="form.blackjack_payout" type="radio" value="6:5" class="radio-input" />
                <span>6:5 — Reduced (pays $12 on $10 bet, +1.4% house edge)</span>
              </label>
            </div>
            <span class="form-hint">{{ $t('admin.config.blackjack.hint_blackjack_payout') }}</span>
          </div>

          <div class="rtp-display">
            <span class="rtp-label">{{ $t('admin.config.label_est_rtp') }}</span>
            <span class="rtp-value">{{ rtp }}%</span>
          </div>
        </div>
      </section>

      <!-- Dealer Rules -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.blackjack.section_dealer') }}</h3>

        <div class="form-row">
          <label class="toggle-label">
            <input v-model="form.hit_soft_17" type="checkbox" class="toggle-checkbox" />
            <span>
              {{ $t('admin.config.blackjack.label_hit_soft_17') }}
              <span class="rule-badge" :class="form.hit_soft_17 ? 'badge-warning' : 'badge-info'">
                {{ form.hit_soft_17 ? 'H17 — +0.22% house edge' : 'S17 — dealer stands on all 17s' }}
              </span>
            </span>
          </label>
        </div>

        <div class="form-row">
          <label class="toggle-label">
            <input v-model="form.peek_rule" type="checkbox" class="toggle-checkbox" />
            <span>
              {{ $t('admin.config.blackjack.label_peek_rule') }}
              <span class="rule-badge badge-info">
                {{ form.peek_rule ? 'Dealer peeks for BJ on Ace/10' : 'No peek (European rules)' }}
              </span>
            </span>
          </label>
        </div>
        <p class="form-hint" style="margin-top: 4px;">
          {{ $t('admin.config.blackjack.hint_peek_rule') }}
        </p>
      </section>

      <!-- Double Down Rules -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.blackjack.section_double_down') }}</h3>

        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.blackjack.label_double_down_allowed') }}
            <select v-model="form.double_down_allowed" class="form-select">
              <option value="any_two">Any two cards (standard)</option>
              <option value="9_10_11">Hard 9, 10, or 11 only</option>
              <option value="10_11">Hard 10 or 11 only (most restrictive)</option>
            </select>
            <span class="form-hint">{{ $t('admin.config.blackjack.hint_double_down') }}</span>
          </label>
        </div>

        <div class="form-row">
          <label class="toggle-label">
            <input v-model="form.double_after_split" type="checkbox" class="toggle-checkbox" />
            <span>{{ $t('admin.config.blackjack.label_double_after_split') }}</span>
          </label>
        </div>
      </section>

      <!-- Split Rules -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.blackjack.section_split') }}</h3>

        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.blackjack.label_max_splits') }}
            <select v-model.number="form.max_splits" class="form-select">
              <option :value="1">1 split (2 hands max)</option>
              <option :value="2">2 splits (3 hands max)</option>
              <option :value="3">3 splits (4 hands max) — default</option>
              <option :value="4">4 splits (5 hands max)</option>
            </select>
          </label>
        </div>

        <div class="form-row">
          <label class="toggle-label">
            <input v-model="form.split_aces_one_card" type="checkbox" class="toggle-checkbox" />
            <span>{{ $t('admin.config.blackjack.label_resplit_aces') }}</span>
          </label>
        </div>
      </section>

      <!-- Side Options -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.blackjack.section_side_bets') }}</h3>

        <div class="form-row">
          <label class="toggle-label">
            <input v-model="form.surrender_allowed" type="checkbox" class="toggle-checkbox" />
            <span>{{ $t('admin.config.blackjack.label_surrender') }}</span>
          </label>
        </div>

        <div class="form-row">
          <label class="toggle-label">
            <input v-model="form.insurance_allowed" type="checkbox" class="toggle-checkbox" />
            <span>{{ $t('admin.config.blackjack.label_insurance') }}</span>
          </label>
        </div>
      </section>

      <!-- Bet Limits -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.section_bet_limits') }}</h3>
        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.label_min_bet') }}
            <input
              v-model.number="form.min_bet"
              type="number"
              step="0.01"
              min="0.01"
              class="form-input"
              required
            />
          </label>
          <label class="form-label">
            {{ $t('admin.config.label_max_bet') }}
            <input
              v-model.number="form.max_bet"
              type="number"
              step="0.01"
              min="0.01"
              class="form-input"
              required
            />
          </label>
        </div>
        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.label_max_payout') }}
            <input
              v-model.number="form.max_payout"
              type="number"
              step="0.01"
              min="0"
              class="form-input"
              required
            />
            <span class="form-hint">{{ $t('admin.config.hint_no_cap') }}</span>
          </label>
        </div>
      </section>

      <div v-if="saveError" class="error-banner">{{ saveError }}</div>
      <div v-if="saveSuccess" class="success-banner">{{ saveSuccess }}</div>

      <div class="form-actions">
        <button type="submit" class="btn btn-primary" :disabled="saving">
          {{ saving ? $t('admin.config.saving') : $t('admin.config.save_config') }}
        </button>
        <span v-if="sourceLabel" class="source-badge" :class="sourceClass">
          {{ sourceLabel }}
        </span>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'

interface BlackjackConfigResponse {
  source: 'database' | 'default'
  deck_count: 1 | 2 | 4 | 6 | 8
  penetration_fraction: number
  blackjack_payout: '3:2' | '6:5'
  hit_soft_17: boolean
  double_down_allowed: 'any_two' | '9_10_11' | '10_11'
  double_after_split: boolean
  split_aces_one_card: boolean
  max_splits: 1 | 2 | 3 | 4
  surrender_allowed: boolean
  insurance_allowed: boolean
  peek_rule: boolean
  min_bet: string
  max_bet: string
  max_payout: string
  rtp: number
}

const { t } = useI18n()

const loading = ref(true)
const saving = ref(false)
const loadError = ref('')
const saveError = ref('')
const saveSuccess = ref('')

const source = ref<'database' | 'default'>('default')
const rtp = ref(99.5)

const form = reactive({
  deck_count: 6 as 1 | 2 | 4 | 6 | 8,
  penetration_fraction: 0.75,
  blackjack_payout: '3:2' as '3:2' | '6:5',
  hit_soft_17: true,
  double_down_allowed: 'any_two' as 'any_two' | '9_10_11' | '10_11',
  double_after_split: true,
  split_aces_one_card: true,
  max_splits: 3 as 1 | 2 | 3 | 4,
  surrender_allowed: true,
  insurance_allowed: true,
  peek_rule: true,
  min_bet: '1.00',
  max_bet: '10000.00',
  max_payout: '0.00',
})

const sourceLabel = computed(() => (source.value === 'database' ? t('admin.config.saved_to_db') : t('admin.config.using_defaults')))
const sourceClass = computed(() => (source.value === 'database' ? 'source-db' : 'source-default'))

async function loadConfig() {
  loading.value = true
  loadError.value = ''
  try {
    const res = await apiClient.get<BlackjackConfigResponse>('/admin/api/games/blackjack/config')
    populateForm(res.data)
  } catch {
    loadError.value = t('admin.config.blackjack.error_load')
  } finally {
    loading.value = false
  }
}

function populateForm(data: BlackjackConfigResponse) {
  source.value = data.source
  rtp.value = data.rtp
  form.deck_count = data.deck_count
  form.penetration_fraction = data.penetration_fraction
  form.blackjack_payout = data.blackjack_payout
  form.hit_soft_17 = data.hit_soft_17
  form.double_down_allowed = data.double_down_allowed
  form.double_after_split = data.double_after_split
  form.split_aces_one_card = data.split_aces_one_card
  form.max_splits = data.max_splits
  form.surrender_allowed = data.surrender_allowed
  form.insurance_allowed = data.insurance_allowed
  form.peek_rule = data.peek_rule
  form.min_bet = data.min_bet
  form.max_bet = data.max_bet
  form.max_payout = data.max_payout
}

async function saveConfig() {
  saveError.value = ''
  saveSuccess.value = ''
  saving.value = true

  try {
    const res = await apiClient.post<BlackjackConfigResponse>('/admin/api/games/blackjack/config', {
      deck_count: form.deck_count,
      penetration_fraction: form.penetration_fraction,
      blackjack_payout: form.blackjack_payout,
      hit_soft_17: form.hit_soft_17,
      double_down_allowed: form.double_down_allowed,
      double_after_split: form.double_after_split,
      split_aces_one_card: form.split_aces_one_card,
      max_splits: form.max_splits,
      surrender_allowed: form.surrender_allowed,
      insurance_allowed: form.insurance_allowed,
      peek_rule: form.peek_rule,
      min_bet: form.min_bet,
      max_bet: form.max_bet,
      max_payout: form.max_payout,
    })
    populateForm(res.data)
    saveSuccess.value = t('admin.config.blackjack.save_success')
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string } } }
    saveError.value = e.response?.data?.message ?? t('admin.config.blackjack.error_save')
  } finally {
    saving.value = false
  }
}

onMounted(loadConfig)
</script>

<style scoped>
.bj-admin-config {
  padding: 0;
}

.config-header {
  margin-bottom: 24px;
}

.config-title {
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 6px;
}

.config-subtitle {
  color: var(--text-secondary, #b1bad3);
  font-size: 0.875rem;
  margin: 0;
}

/* Loading / error / success */
.loading-state {
  color: var(--text-secondary, #b1bad3);
  padding: 32px;
  text-align: center;
}

.error-banner {
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: 8px;
  color: var(--color-danger-light, #f87171);
  padding: 12px 16px;
  font-size: 0.875rem;
  margin-bottom: 16px;
}

.success-banner {
  background: rgba(26, 158, 110, 0.12);
  border: 1px solid rgba(26, 158, 110, 0.4);
  border-radius: 8px;
  color: var(--color-success-light, #34d399);
  padding: 12px 16px;
  font-size: 0.875rem;
  margin-bottom: 16px;
}

/* Form */
.settings-form {
  max-width: 720px;
}

.settings-section {
  background: var(--bg-secondary, #213743);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary, #fff);
  margin: 0 0 16px;
}

.form-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
  align-items: flex-start;
}

.form-row:last-child {
  margin-bottom: 0;
}

.form-label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.8rem;
  color: var(--text-secondary, #b1bad3);
  flex: 1;
  min-width: 180px;
}

.form-input,
.form-select {
  padding: 8px 12px;
  background: var(--bg-primary, #0f212e);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 6px;
  color: var(--text-primary, #fff);
  font-size: 0.875rem;
}

.form-hint {
  font-size: 0.72rem;
  color: var(--text-secondary, #b1bad3);
  opacity: 0.7;
}

/* Radio group */
.radio-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 2px;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: var(--text-primary, #fff);
  cursor: pointer;
}

.radio-input {
  accent-color: var(--accent, #1a9e6e);
  width: 15px;
  height: 15px;
}

/* RTP display */
.rtp-display {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  padding-top: 18px;
}

.rtp-label {
  font-size: 0.75rem;
  color: var(--text-secondary, #b1bad3);
}

.rtp-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-success-light, #34d399);
}

/* Rule badge */
.rule-badge {
  display: inline-block;
  font-size: 0.68rem;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 4px;
  margin-left: 6px;
}

.badge-warning {
  background: rgba(251, 191, 36, 0.15);
  color: var(--color-warning-light, #fbbf24);
}

.badge-info {
  background: rgba(96, 165, 250, 0.15);
  color: var(--color-info-light, #60a5fa);
}

/* Toggle */
.toggle-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.875rem;
  color: var(--text-primary, #fff);
  cursor: pointer;
  flex: 1;
  min-width: 200px;
}

.toggle-checkbox {
  width: 16px;
  height: 16px;
  accent-color: var(--accent, #1a9e6e);
  flex-shrink: 0;
}

/* Actions row */
.form-actions {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 8px;
}

/* Source badge */
.source-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
}

.source-db {
  background: rgba(59, 130, 246, 0.2);
  color: var(--color-info-light, #60a5fa);
}

.source-default {
  background: rgba(107, 114, 128, 0.2);
  color: var(--color-muted, #9ca3af);
}

/* Buttons */
.btn {
  padding: 9px 20px;
  border-radius: 7px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: opacity 0.15s;
}

.btn:disabled {
  opacity: 0.55;
  cursor: default;
}

.btn-primary {
  background: var(--accent, #1a9e6e);
  color: var(--color-on-dark, #fff);
}
</style>
